import React, { useEffect, useState, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { compose } from 'redux';
import { connect } from 'react-redux';
import get from 'lodash/get';
import PropTypes from 'prop-types';

import Input from '../../../../components/Form/Input';
import Button from '../../../../components/Button';
import Select from '../../../../components/Form/Select';
import VerifyAddress, { Popup } from '../../../../components/VerifyAddress';
import { states } from '../../../../utils/selectOptions';
import { maskZipCodes, matchZipCodes } from '../../../../utils/masks';
import { nextAction } from '../../../../saga/actions/workflow';
import { getHTTPErrorMessage } from '../../../../utils/fetch';
import { verifyAddress } from '../../../../utils/verifyAddress';
import { unSupportedStates } from '../../../../utils/unSupportedStates';
import { getNextUrl } from '../url';

let callSmartyOnSubmit = false;

const Step4 = (props) => {
  const { workflow } = props;
  // get functions to build form with useForm() hook
  const hookForm = useForm({ reValidateMode: 'onSubmit' });
  const { handleSubmit, setValue, setError, clearErrors, getValues } = hookForm;

  const [isPrefilled, setIsPrefilled] = useState(false);
  const [isVAVisible, setVAVisible] = useState(false); // verify address
  const [isVAVerified, setVAVerified] = useState(false);
  const [isVALoading, setVALoading] = useState(false);
  const [popupType, setPopupType] = useState(0);
  const [isAddressPopupVisible, setPopupVisible] = useState(false);

  // set initial values
  const address = useMemo(() => get(workflow, 'data.data.address'), [workflow]);
  const city = useMemo(() => get(workflow, 'data.data.city'), [workflow]);
  const state = useMemo(() => get(workflow, 'data.data.state'), [workflow]);
  const zipCode = useMemo(() => get(workflow, 'data.data.zipCode'), [workflow]);
  const errorMsg = useMemo(() => getHTTPErrorMessage(workflow.error), [workflow]);

  useEffect(() => {
    setValue('address', address);
    setValue('city', city);
    setValue('state', state);
    setValue('zipCode', zipCode);
    if (address || city || state || zipCode) {
      setIsPrefilled(true);
    }
  }, [address, city, state, zipCode, setValue]);

  // handle functions
  const doNext = (data) => {
    props.nextAction({
      url: getNextUrl(),
      method: 'PUT',
      data: {
        Address: data.address,
        City: data.city,
        State: data.state,
        ZipCode: data.zipCode,
      },
    });
  };

  const verifySmartAddress = () => {
    const zipcode = getValues('zipCode');
    const addressVal = getValues('address');
    const cityVal = getValues('city');
    const stateVal = getValues('state');
    setVAVerified(false);
    setVALoading(true);

    verifyAddress({ zipcode, address: addressVal, city: cityVal, state: stateVal })
      .then((response) => {
        setVALoading(false);
        if (response) {
          if (get(response, 'metadata.recordType') === 'P') {
            setVAVerified(false);
            setPopupVisible(true);
            setPopupType(3);
            setError('address', { message: 'Address is not valid. Please enter valid address.' });
            callSmartyOnSubmit = false;
          } else {
            const addressData = {
              address: response.deliveryLine1,
              city: get(response, 'components.cityName'),
              state: get(response, 'components.state'),
              zipcode: `${response.deliveryPointBarcode}`.slice(0, 5),
            };
            setVAVerified(true);
            clearErrors('address');
            clearErrors('zipCode');
            clearErrors('city');
            clearErrors('state');
            setValue('address', addressData.address);
            setValue('city', addressData.city);
            setValue('state', addressData.state);
            setValue('zipCode', addressData.zipcode);
            if (callSmartyOnSubmit) {
              callSmartyOnSubmit = false;
              doNext({
                address: addressData.address,
                city: addressData.city,
                state: addressData.state,
                zipCode: addressData.zipcode,
              });
            }
          }
        } else {
          setVAVerified(false);
          setPopupVisible(true);
          setPopupType(2);
          setError('address', { message: 'Address is not valid. Please enter valid address.' });
          callSmartyOnSubmit = false;
        }
      }).catch((error) => {
        console.log('verify address error', error);
        setVAVerified(false);
        setPopupVisible(true);
        setPopupType(1);
        setVALoading(false);
        setError('address', { message: 'Address is not valid. Please enter valid address.' });
        callSmartyOnSubmit = false;
      });
  };

  const onSubmit = (data) => {
    if (isVAVisible && !isVAVerified) {
      callSmartyOnSubmit = true;
      verifySmartAddress();
      return false;
    }
    doNext(data);
    return false;
  };

  const handleChange = () => {
    clearErrors('address');
    setVAVisible(true);
    setVAVerified(false);
    setVALoading(false);
  };

  const handleUndo = (e) => {
    e.preventDefault();
    setVAVerified(false);
    setVALoading(false);
  };

  const handleAbort = () => {
    setPopupVisible(false);
  };

  const handleVerifyAddressClick = (e) => {
    if (e) {
      e.preventDefault();
    }
    const addressVal = getValues('address');
    const stateVal = getValues('state');
    if (unSupportedStates.indexOf(stateVal) !== -1 && stateVal !== 'NY') {
      setPopupVisible(true);
      setPopupType(4);
      setVAVerified(false);
      setVALoading(false);
      setError('address', { message: 'Address is not valid. Please enter valid address.' });
      callSmartyOnSubmit = false;
    } else if (!addressVal) {
      setPopupVisible(true);
      setPopupType(1);
      setError('address', { message: 'Address is not valid. Please enter valid address.' });
      callSmartyOnSubmit = false;
    } else {
      verifySmartAddress();
    }
  };

  const validateZipcode = (value) => {
    if (!value) {
      return 'This input field is required';
    } if (!matchZipCodes.test(value)) {
      return 'Invalid zipCode';
    }
    return null;
  };

  const validateState = (val) => {
    if (unSupportedStates.indexOf(val) !== -1) {
      return 'Unsupported State';
    }
    return null;
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="text-center md:text-left font-bold text-[26px] leading-[26px] md:text-[32px] md:leading-[38px] text-[#263952]">
        We need some
        {' '}
        <br />
        {' '}
        personal details from you.
      </div>
      <div className="text-center md:text-left font-normal text-[15px] leading-[20px] text-[#263952] mt-[8px] ">
        This won’t affect your credit score
        {' '}
        <sup>1</sup>
      </div>
      { isPrefilled
        ? (
          <>
            <div className="text-center md:text-left font-medium text-[18px] leading-[32px] text-black mt-[53px]">
              Please confirm your address.
            </div>
            <div className="text-center md:text-left font-normal text-[12px] leading-[20px] text-[#9B9B9B] mt-[5px] ">
              You can edit this information as needed.
            </div>
          </>
        )
        : (
          <div className="text-center md:text-left font-medium text-[18px] leading-[32px] text-black mt-[53px]">
            What&apos;s your address?
          </div>
        )}
      <div className="relative">
        <Input
          autoFocus
          name="address"
          label="Street Address"
          className="w-full mt-[20px]"
          isMasked={false}
          hookForm={hookForm}
          registerOptions={{ required: 'Street is required', onChange: handleChange }}
        />
        {isVAVisible
          && (
          <VerifyAddress
            className="absolute right-[4px] top-[39px] md:top-[34px]"
            isVerified={isVAVerified}
            isLoading={isVALoading}
            onClick={isVAVerified ? handleUndo : handleVerifyAddressClick}
          />
          )}
        {isAddressPopupVisible
          && (
          <Popup
            handleAbort={handleAbort}
            type={popupType}
            data={{
              address: getValues('address'),
              city: getValues('city'),
              state: getValues('state'),
              zipcode: getValues('zipCode'),
            }}
          />
          )}
      </div>
      <div className="flex w-full flex-wrap justify-between">
        <div className="w-3/5 md:w-2/5">
          <Input
            name="city"
            label="City"
            isMasked={false}
            hookForm={hookForm}
            registerOptions={{ required: 'City is required', onChange: handleChange }}
          />
        </div>
        <div className="w-1/3 md:w-1/4">
          <Select
            name="state"
            label="State"
            data={states}
            hookForm={hookForm}
            onChange={handleChange}
            registerOptions={{ required: 'State is required', validate: validateState }}
          />
        </div>
        <div className="w-1/4">
          <Input
            name="zipCode"
            label="Zip Code"
            isMasked={maskZipCodes}
            hookForm={hookForm}
            validate={validateZipcode}
            onCustomChange={handleChange}
          />
        </div>
      </div>
      <Button isLoading={workflow.isLoading} type="submit" className="w-full md:w-[240px]">Continue</Button>
      {errorMsg && <div className="mt-3 mb-3 text-[#d71f28] text-xs">{errorMsg}</div>}
    </form>
  );
};

Step4.propTypes = {
  workflow: PropTypes.object.isRequired,
  nextAction: PropTypes.func.isRequired,
};

export default compose(
  connect(
    (state) => ({
      workflow: state.workflow,
    }),
    {
      nextAction,
    }
  )
)(Step4);
